/**
 * Hook para estado e carregamento dos logs de auditoria no modal de detalhes da OS.
 * Usado pelo OrderDetailsModal (ou pelo conteúdo do modal) para alternar vista detalhes ↔ logs.
 */

import { useState, useCallback, useEffect } from 'react';
import { getApiBaseUrl } from '../../../utils/apiConfig';

export function useOrderDetailsAuditLogs(order) {
  const orderId = order?.id;
  const [showLogsView, setShowLogsView] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [auditLogsError, setAuditLogsError] = useState(null);

  useEffect(() => {
    setShowLogsView(false);
    setAuditLogs([]);
    setAuditLogsError(null);
  }, [orderId]);

  const fetchAuditLogs = useCallback(async () => {
    if (!orderId) return;
    setLoadingAuditLogs(true);
    setAuditLogsError(null);
    try {
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setAuditLogsError('Sessão inválida');
        return;
      }
      const res = await fetch(
        `${getApiBaseUrl()}/api/audit/logs?ordemId=${encodeURIComponent(orderId)}&limit=200`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const json = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(json.logs)) {
        setAuditLogs(json.logs);
      } else {
        setAuditLogsError(json.message || 'Erro ao carregar logs');
        setAuditLogs([]);
      }
    } catch (err) {
      setAuditLogsError(err.message || 'Erro ao carregar logs');
      setAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  }, [orderId]);

  const handleToggleLogs = useCallback(() => {
    setShowLogsView((prev) => {
      if (!prev) fetchAuditLogs();
      return !prev;
    });
  }, [fetchAuditLogs]);

  const openLogsView = useCallback(() => {
    setShowLogsView(true);
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const closeLogsView = useCallback(() => setShowLogsView(false), []);

  return {
    showLogsView,
    auditLogs,
    loadingAuditLogs,
    auditLogsError,
    handleToggleLogs,
    openLogsView,
    closeLogsView,
  };
}
